import { useState, useEffect, useRef, type ReactNode } from 'react';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { functions } from '../../lib/firebase';
import { X, Phone, Mail, XCircle, CalendarPlus, Check } from 'lucide-react';
import { useClientDetail, type ClientDetailEvent, type ClientContactEntry, type ClientLoanDetail } from '../../hooks/useClientDetail';
import { AddContactPanel, type ContactSubmission } from './AddContactPanel';
import { EVENT_LABELS, eventTitle } from './eventTitle';
import { DatePickerField } from '../common/DatePickerField';

interface ClientDetailDrawerProps {
  companyId?: string;
  clientId: string | null;
  onClose: () => void;
  /** Called after a mutation so the list can refresh (lastContact, dob, …). */
  onChanged?: () => void;
  /**
   * Open a DIFFERENT client in this same drawer. Supplied by the page that owns
   * the selected-client state; without it the merge redirect below still tells
   * the user what happened, it just cannot navigate for them.
   */
  onOpenClient?: (clientId: string) => void;
}

const OPEN_STATUSES = ['pending', 'prompting', 'prompted', 'escalating', 'escalated'];

function fmt(v: unknown): string {
  if (!v) return '—';
  let ms: number | null = null;
  if (typeof v === 'object' && v !== null && typeof (v as { _seconds?: number })._seconds === 'number') {
    ms = (v as { _seconds: number })._seconds * 1000;
  } else if (typeof v === 'string') {
    return v.length >= 10 ? v.slice(0, 10) : v;
  } else if (typeof v === 'number') {
    ms = v;
  }
  if (ms == null || Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusChipClass(status: string): string {
  if (status === 'actioned') return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (status === 'escalated' || status === 'escalating') return 'bg-red-500/20 text-red-400 border-red-500/30';
  if (status === 'prompted' || status === 'prompting') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
  if (status === 'dismissed') return 'bg-gray-700/40 text-gray-400 border-gray-600/40';
  return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
}

function Kv({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-bold">{label}</div>
      <div className="text-sm text-white mt-0.5">{value}</div>
    </div>
  );
}

function LoanRow({ loan }: { loan: ClientLoanDetail }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-700/50 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium truncate">{loan.lender ?? 'Unknown lender'}</div>
        <div className="text-xs text-gray-500">
          Settled {loan.settlementDate ?? 'N/A'} · Loan {loan.loanId}
        </div>
      </div>
      <span
        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${
          loan.isActive
            ? 'bg-green-500/20 text-green-400 border-green-500/30'
            : 'bg-gray-700/40 text-gray-400 border-gray-600/40'
        }`}
      >
        {loan.isActive ? 'Active' : 'Discharged'}
      </span>
    </div>
  );
}

function EventRow({
  ev,
  onDismiss,
  onComplete,
  dismissing,
  completing,
}: {
  ev: ClientDetailEvent;
  onDismiss?: (eventId: string) => void;
  /** Trail-risk only: bind the contact form to THIS occurrence. */
  onComplete?: (ev: ClientDetailEvent) => void;
  dismissing?: boolean;
  completing?: boolean;
}) {
  const canDismiss = OPEN_STATUSES.includes(ev.status);
  const isTrailRisk = ev.eventFamily === 'trail_risk';
  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-700/50 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium">{eventTitle(ev)}</div>
        <div className="text-xs text-gray-500">Due {fmt(ev.dueAt)}</div>
      </div>
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${statusChipClass(ev.status)}`}>{ev.status}</span>
      {/* Complete is offered for trail-risk ONLY, because only those carry an
          occurrence a contact can be attributed to. A cadence reminder closes by
          logging any contact, so a second button there would be noise. Listed
          BEFORE Dismiss on purpose: recording an outcome should be the easier of
          the two, not the afterthought. */}
      {canDismiss && isTrailRisk && onComplete && (
        <button
          type="button"
          onClick={() => onComplete(ev)}
          disabled={completing}
          title="Log a contact and complete this follow-up"
          className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="w-4 h-4" />
          Complete
        </button>
      )}
      {canDismiss && onDismiss && (
        <button
          type="button"
          onClick={() => onDismiss(ev.eventId)}
          disabled={dismissing}
          title="Dismiss this care event"
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <XCircle className="w-4 h-4" />
          Dismiss
        </button>
      )}
    </div>
  );
}

/**
 * The contact form bound to ONE occurrence, rendered directly under its row.
 *
 * Saving here is what completes the event: the server closes a trail-risk event
 * only when the contact names its exact eventId. The banner says so, because
 * without it the form looks identical whether it will complete one specific
 * month or nothing at all, which had a user log the same contact twice
 * wondering why an alert stayed pending.
 */
function CompleteEventPanel({
  ev,
  saving,
  onSubmit,
  onCancel,
}: {
  ev: ClientDetailEvent;
  saving: boolean;
  onSubmit: (s: ContactSubmission) => void | Promise<void>;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // The row can sit near the bottom of a long timeline; bring the form fully
  // into view. Optional-called because jsdom does not implement scrollIntoView.
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, []);
  return (
    <div
      ref={ref}
      data-testid="complete-event-panel"
      className="my-2 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-xs text-blue-200">
          Log the contact to <span className="font-semibold">complete</span> {eventTitle(ev)}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-gray-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
      </div>
      {/* No "re: event type" picker: the server ignores eventType when an
          eventId is present, so offering it would be a lie. */}
      <AddContactPanel saving={saving} eventTypes={[]} onSubmit={onSubmit} />
    </div>
  );
}

function ContactRow({ c }: { c: ClientContactEntry }) {
  const when = c.contactedByPhone ? c.phoneDate : c.emailDate;
  return (
    <div className="py-2.5 border-b border-gray-700/50 last:border-0">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        {c.contactedByPhone && (
          <span className="inline-flex items-center gap-1 text-violet-400">
            <Phone className="w-3 h-3" /> Phone
          </span>
        )}
        {c.contactedByEmail && (
          <span className="inline-flex items-center gap-1 text-amber-400">
            <Mail className="w-3 h-3" /> Email
          </span>
        )}
        <span className="text-gray-500">·</span>
        <span>{when ? when.slice(0, 10) : fmt(c.createdAt)}</span>
        {c.createdBy && (
          <>
            <span className="text-gray-500">·</span>
            <span className="truncate">{c.createdBy}</span>
          </>
        )}
      </div>
      {c.comments && <div className="text-sm text-gray-200 mt-1 whitespace-pre-wrap">{c.comments}</div>}
    </div>
  );
}

/**
 * Mirrors `ClientDocumentSchema.notes` (packages/shared-schemas/src/clientCare.ts),
 * which upsertClient now enforces server-side. Kept local so a client-only change
 * does not touch packages/ (that forces every codebase to redeploy).
 */
const CLIENT_NOTES_MAX_LENGTH = 10000;

const inputClass =
  'w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500';

export function ClientDetailDrawer({ companyId, clientId, onClose, onChanged, onOpenClient }: ClientDetailDrawerProps) {
  const { client, events, contacts, capabilities, loans, mergedInto, loading, error, refetch } =
    useClientDetail(companyId, clientId);
  const [saving, setSaving] = useState(false);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  /** The trail-risk occurrence the contact form is currently bound to, if any. */
  const [completingEvent, setCompletingEvent] = useState<ClientDetailEvent | null>(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpDue, setFollowUpDue] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [creatingFollowUp, setCreatingFollowUp] = useState(false);
  const open = clientId !== null;

  // Editable contact details — stored on the client (survives broker turnover),
  // manual-only so the daily materializer never overwrites them.
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  // Standing facts about the client ("2 children, renovating next year"), as
  // opposed to a dated contact in the history below.
  const [notes, setNotes] = useState('');

  // Re-seed the editor only when a different client is opened (not on every
  // refetch — that would clobber an in-progress edit). Save→refetch keeps the
  // typed values, which already equal what was persisted.
  useEffect(() => {
    setContactName(client?.primaryContactName ?? '');
    setPhone(client?.phone ?? '');
    setEmail(client?.email ?? '');
    setDob(client?.dob ?? '');
    setNotes(client?.notes ?? '');
    // The drawer stays mounted between clients, so an in-progress Complete from
    // the previous client must not carry over: it hid this client's general
    // "Log a contact" form behind a banner naming an event that is not on its
    // timeline (LMC, 2026-10-08).
    setCompletingEvent(null);
    setShowFollowUp(false);
    setFollowUpDue('');
    setFollowUpNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.clientId]);

  const logContact = async (s: ContactSubmission) => {
    if (!companyId || !clientId) return;
    setSaving(true);
    try {
      await httpsCallable(functions, 'logClientContact')({
        companyId,
        clientId,
        contactedByPhone: s.contactedByPhone,
        contactedByEmail: s.contactedByEmail,
        phoneDate: s.phoneDate,
        emailDate: s.emailDate,
        comments: s.comments,
        // An exact eventId when the user chose "Complete" on a specific
        // occurrence; the server ignores eventType when eventId is present.
        // Trail-risk events can ONLY be closed this way: a general contact
        // deliberately cannot close them, or one call would close every open
        // month for the client at once.
        ...(completingEvent ? { eventId: completingEvent.eventId } : { eventType: s.eventType }),
      });
      const method =
        s.contactedByPhone && s.contactedByEmail ? 'phone & email' : s.contactedByEmail ? 'email' : 'phone';
      toast.success(
        completingEvent
          ? `Logged ${method} contact and completed ${eventTitle(completingEvent)}`
          : `Logged ${method} contact for ${client?.displayName ?? 'client'}`,
      );
      setCompletingEvent(null);
      await refetch();
      onChanged?.();
    } catch (e) {
      console.error('[logClientContact] failed:', e);
      toast.error(e instanceof Error ? e.message : 'Failed to log contact. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = async () => {
    if (!companyId || !clientId) return;
    const patch: Record<string, string | null> = {};
    if (contactName.trim() !== (client?.primaryContactName ?? '')) patch.primaryContactName = contactName.trim() || null;
    if (phone.trim() !== (client?.phone ?? '')) patch.phone = phone.trim() || null;
    if (email.trim() !== (client?.email ?? '')) patch.email = email.trim() || null;
    if (dob !== (client?.dob ?? '')) patch.dob = dob || null;
    if (notes.trim() !== (client?.notes ?? '')) patch.notes = notes.trim() || null;
    if (Object.keys(patch).length === 0) {
      toast('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await httpsCallable(functions, 'upsertClient')({ companyId, clientId, patch });
      toast.success('Client details saved');
      await refetch();
      onChanged?.();
    } catch (e) {
      console.error('[upsertClient] failed:', e);
      toast.error('Failed to save client details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Dismiss a care event, with a REQUIRED reason.
   *
   * The server made `dismissReason` mandatory (2026-09): a dismissal with no
   * reason destroys the signal the retention reports exist to produce, and is the
   * only way to tell a genuine false positive from a queue being tidied away.
   * This caller has to collect one, or every dismissal fails with
   * `invalid-argument`.
   *
   * A native prompt is deliberate for now: it is the smallest change that keeps
   * the button working, and a styled modal here is a UI task that should not gate
   * the correctness fix. Cancelling the prompt cancels the dismissal, which is
   * the safe default.
   */
  const dismissEvent = async (eventId: string) => {
    if (!companyId || !clientId) return;
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      'Why are you dismissing this follow-up?\n\n(e.g. "duplicate facility", "client already contacted offline", "raised in error")',
    );
    if (reason === null) return; // cancelled: not a dismissal
    const dismissReason = reason.trim();
    if (!dismissReason) {
      toast.error('A reason is required to dismiss a follow-up.');
      return;
    }
    setDismissingId(eventId);
    try {
      await httpsCallable(functions, 'dismissClientEvent')({
        companyId,
        clientId,
        eventId,
        dismissReason,
      });
      toast.success('Care event dismissed');
      await refetch();
      onChanged?.();
    } catch (e) {
      console.error('[dismissClientEvent] failed:', e);
      // Surface the server's own message rather than a generic retry prompt: a
      // rejected reason or a stale event is not fixed by trying again.
      const msg = e instanceof Error && e.message ? e.message : 'Failed to dismiss event.';
      toast.error(msg);
    } finally {
      setDismissingId(null);
    }
  };

  const createFollowUp = async () => {
    if (!companyId || !clientId) return;
    if (!followUpDue) {
      toast.error('Pick a due date for the follow-up');
      return;
    }
    setCreatingFollowUp(true);
    try {
      await httpsCallable(functions, 'createFollowUpTask')({
        companyId,
        clientId,
        dueDate: followUpDue,
        note: followUpNote.trim() || undefined,
      });
      toast.success('Follow-up scheduled');
      setShowFollowUp(false);
      setFollowUpDue('');
      setFollowUpNote('');
      await refetch();
      onChanged?.();
    } catch (e) {
      console.error('[createFollowUpTask] failed:', e);
      toast.error('Failed to schedule follow-up. Please try again.');
    } finally {
      setCreatingFollowUp(false);
    }
  };

  const dirty =
    contactName.trim() !== (client?.primaryContactName ?? '') ||
    phone.trim() !== (client?.phone ?? '') ||
    email.trim() !== (client?.email ?? '') ||
    dob !== (client?.dob ?? '') ||
    notes.trim() !== (client?.notes ?? '');

  // Trail-risk types are EXCLUDED from the "re: event type" picker on purpose.
  //
  // The server now closes trail-risk events only by exact eventId, because those
  // are keyed per loan AND per period: an eventType-wide close would shut every
  // open arrears month for the client in one go. Leaving them in this dropdown
  // would offer an action the server correctly refuses to perform, and the user
  // would log a contact and see nothing close, with no error to explain it.
  //
  // Closing a specific occurrence is done from its own report row (see
  // TrailRiskFollowUp). Occurrence-level choices in this drawer are the intended
  // follow-up: getClientDetail already returns eventFamily and the trail-risk
  // loan/period for it, the UI is simply not built yet.
  const openEventTypes = Array.from(
    new Set(
      events
        .filter((e) => OPEN_STATUSES.includes(e.status) && e.eventFamily !== 'trail_risk')
        .map((e) => e.eventType),
    ),
  ).map((t) => ({ value: t, label: EVENT_LABELS[t] ?? t }));

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 h-screen w-full sm:max-w-xl lg:max-w-[min(42rem,60vw)] xl:max-w-[min(48rem,55vw)] bg-gray-800 border-l border-gray-700 z-50 overflow-auto transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {open && (
          <>
            <div className="flex items-center gap-3 p-4 sm:p-5 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <div className="flex-1 min-w-0">
                <div className="text-lg font-bold text-white truncate">{client?.displayName ?? (loading ? 'Loading…' : 'Client')}</div>
                {client && (
                  <div className="text-xs text-gray-400">
                    {client.primaryBrokerDisplay ?? '—'} · {client.loanIds.length} loan(s)
                  </div>
                )}
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded bg-gray-900 border border-gray-700 text-gray-400 flex items-center justify-center hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            {error && <div className="m-4 bg-red-900/30 border border-red-500/30 text-red-300 rounded-lg p-3 text-sm">{error}</div>}

            {/* Merge redirect. The server deliberately withholds the tombstone's
                profile, so there is nothing to render here but the forwarding
                address — which is the point: the live details live on the
                surviving client, and this is how someone arriving from a stale
                link or an old digest gets there. */}
            {mergedInto && (
              <div className="m-4 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <div className="text-sm font-semibold text-blue-200">This client was merged</div>
                <div className="text-sm text-gray-300 mt-1">
                  Their loans, reminders and contact history moved to the surviving client record.
                </div>
                {onOpenClient && (
                  <button
                    type="button"
                    onClick={() => onOpenClient(mergedInto)}
                    className="mt-3 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium"
                  >
                    Open the surviving client
                  </button>
                )}
              </div>
            )}

            {client && (
              <>
                <div className="p-4 sm:p-5 border-b border-gray-700">
                  <h3 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-3">Profile</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Kv label="Broker" value={client.primaryBrokerDisplay ?? '—'} />
                    <Kv label="Settled" value={client.earliestSettlementDate ?? 'N/A'} />
                    <Kv label="Last contact" value={fmt(client.lastContactAt)} />
                    <Kv label="Status" value={client.hasActiveLoan ? 'Active' : client.status} />
                  </div>
                </div>

                <div className="p-4 sm:p-5 border-b border-gray-700">
                  <h3 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-3">
                    Loans ({loans.length || client.loanIds.length})
                  </h3>
                  {loans.length === 0 ? (
                    <div className="text-sm text-gray-500">
                      {client.loanIds.length > 0 ? 'Loan detail unavailable right now.' : 'No loans on record.'}
                    </div>
                  ) : (
                    <div>
                      {loans.map((loan) => (
                        <LoanRow key={loan.loanId} loan={loan} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 sm:p-5 border-b border-gray-700">
                  <h3 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-3">Client details</h3>
                  {capabilities?.canEditProfile ? (
                    <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">Primary contact</label>
                      <input
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="Person to call — e.g. for a company / trust borrower"
                        className={inputClass}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">Phone</label>
                        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="—" className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">Email</label>
                        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="—" className={inputClass} />
                      </div>
                    </div>
                    <div className="max-w-[14rem]">
                      <DatePickerField
                        id="client-dob"
                        label="Birthday"
                        value={dob}
                        onChange={setDob}
                        disableFuture
                        showYearDropdown
                        fromYear={1920}
                        compact
                        placeholder="dd/mm/yyyy"
                      />
                    </div>
                    <div>
                      <div className="flex items-baseline justify-between mb-1">
                        <label
                          htmlFor="client-notes"
                          className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold"
                        >
                          Client notes
                        </label>
                        {notes.length > CLIENT_NOTES_MAX_LENGTH * 0.9 && (
                          <span className="text-[11px] text-gray-500">
                            {notes.length.toLocaleString('en-AU')} / {CLIENT_NOTES_MAX_LENGTH.toLocaleString('en-AU')}
                          </span>
                        )}
                      </div>
                      {/* Grows with its content (field-sizing) from 6rem up to
                          24rem or 40% of the viewport height, whichever is
                          smaller, then scrolls: on a short laptop a long note must
                          not push the contact log out of view. Browsers without
                          field-sizing fall back to 5 rows with a resize handle. */}
                      <textarea
                        id="client-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        maxLength={CLIENT_NOTES_MAX_LENGTH}
                        rows={5}
                        placeholder="Household, goals, preferences… e.g. 2 children, planning to renovate in 2027"
                        className={`${inputClass} min-h-[6rem] max-h-[min(24rem,40vh)] resize-y [field-sizing:content] leading-relaxed`}
                      />
                    </div>
                    <button
                      onClick={saveDetails}
                      disabled={saving || !dirty}
                      title={!dirty ? 'No changes to save' : undefined}
                      className="px-3 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
                    >
                      {saving ? 'Saving…' : 'Save details'}
                    </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Kv label="Primary contact" value={client.primaryContactName ?? '—'} />
                        <Kv label="Phone" value={client.phone ?? '—'} />
                        <Kv label="Email" value={client.email ?? '—'} />
                        <Kv label="Birthday" value={client.dob ?? '—'} />
                      </div>
                      <Kv
                        label="Client notes"
                        value={
                          client.notes ? (
                            <div className="whitespace-pre-wrap break-words">{client.notes}</div>
                          ) : (
                            '—'
                          )
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="p-4 sm:p-5 border-b border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold">Care timeline</h3>
                    {capabilities?.canCreateFollowUp && (
                      <button
                        type="button"
                        onClick={() => setShowFollowUp((s) => !s)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <CalendarPlus className="w-4 h-4" />
                        Add follow-up
                      </button>
                    )}
                  </div>

                  {showFollowUp && capabilities?.canCreateFollowUp && (
                    <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-4 space-y-3 mb-3">
                      <div className="max-w-[14rem]">
                        <DatePickerField
                          id="follow-up-due"
                          label="Due date"
                          value={followUpDue}
                          onChange={setFollowUpDue}
                          compact
                          placeholder="dd/mm/yyyy"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">Note</label>
                        <textarea
                          value={followUpNote}
                          onChange={(e) => setFollowUpNote(e.target.value)}
                          placeholder="What to follow up on, e.g. confirm refinance options…"
                          rows={3}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white resize-y placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={createFollowUp}
                          disabled={creatingFollowUp || !followUpDue}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
                        >
                          <Check className="w-4 h-4" />
                          {creatingFollowUp ? 'Scheduling…' : 'Schedule follow-up'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowFollowUp(false)}
                          className="text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {events.length === 0 ? (
                    <div className="text-sm text-gray-500">No care events yet.</div>
                  ) : (
                    events.map((ev) => (
                      <div key={ev.eventId} data-testid={`care-event-${ev.eventId}`}>
                        <EventRow
                          ev={ev}
                          onDismiss={capabilities?.canDismissEvent ? dismissEvent : undefined}
                          onComplete={capabilities?.canLogActivity ? setCompletingEvent : undefined}
                          dismissing={dismissingId === ev.eventId}
                          completing={completingEvent?.eventId === ev.eventId}
                        />
                        {/* The bound contact form opens directly under the row the
                            user clicked Complete on. It used to live at the foot of
                            the drawer, below the contact history and out of view, so
                            Complete looked like a button that only greyed itself out
                            (LMC, 2026-09-02 and again 2026-10-06). */}
                        {completingEvent?.eventId === ev.eventId && capabilities?.canLogActivity && (
                          <CompleteEventPanel
                            ev={ev}
                            saving={saving}
                            onSubmit={logContact}
                            onCancel={() => setCompletingEvent(null)}
                          />
                        )}
                      </div>
                    ))
                  )}
                </div>

                <div className="p-4 sm:p-5 border-b border-gray-700">
                  <h3 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-2">
                    Contact history{contacts.length > 0 ? ` (${contacts.length})` : ''}
                  </h3>
                  {contacts.length === 0 ? (
                    <div className="text-sm text-gray-500">No contacts logged yet.</div>
                  ) : (
                    contacts.map((c) => <ContactRow key={c.id} c={c} />)
                  )}
                </div>

                <div className="p-4 sm:p-5">
                  <h3 className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-3">Log a contact</h3>
                  {capabilities?.canLogActivity ? (
                    // While a Complete is in progress this general form steps aside:
                    // a submit here would still close the bound event (logContact
                    // sends its eventId), so two live forms would be two ways to do
                    // the same thing with only one of them labelled.
                    completingEvent ? (
                      <div className="text-sm text-gray-500">
                        Finish completing {eventTitle(completingEvent)} in the Care timeline above, or
                        cancel it there to log a general contact.
                      </div>
                    ) : (
                      <AddContactPanel saving={saving} eventTypes={openEventTypes} onSubmit={logContact} />
                    )
                  ) : (
                    <div className="text-sm text-gray-500">
                      Contact activity is read-only for this client.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
